import React, { useState } from 'react'
import { CheckCircle2, XCircle, Send } from 'lucide-react'
import KaTeXRenderer from '../../../components/common/KaTeXRenderer'
import { resultApi } from '../api/lessonBuilderApi'
import type { QuizCard } from '../types/LessonDoc'

interface Props {
  card: QuizCard
  lessonId?: string
  lessonSlug?: string
}

const norm = (s: any) => String(s ?? '').trim().toLowerCase()

// Trình làm quiz tương tác: chọn đáp án → Nộp bài → chấm điểm + lưu kết quả.
const QuizPlayer: React.FC<Props> = ({ card, lessonId, lessonSlug }) => {
  const questions = card.questions || []
  const [answers, setAnswers] = useState<Record<number, any>>({})
  const [submitted, setSubmitted] = useState(false)
  const [score, setScore] = useState(0)
  const [maxScore, setMaxScore] = useState(0)
  const [correctCount, setCorrectCount] = useState(0)

  const setAns = (qi: number, v: any) => setAnswers((a) => ({ ...a, [qi]: v }))

  const isCorrect = (q: any, given: any): boolean => {
    if (given === undefined || given === null || given === '') return false
    if (q.type === 'multiple_choice') return Number(given) === Number(q.correct)
    if (q.type === 'true_false') return given === q.correct
    if (q.type === 'fill_blank') {
      const cands = [q.answer, ...(q.alternatives || [])].map(norm).filter(Boolean)
      return cands.includes(norm(given))
    }
    return false
  }

  const handleSubmit = () => {
    let sc = 0
    let mx = 0
    let cor = 0
    const detail = questions.map((q: any, qi: number) => {
      const pts = q.points || 1
      mx += pts
      const ok = isCorrect(q, answers[qi])
      if (ok) {
        sc += pts
        cor += 1
      }
      return { qIndex: qi, given: answers[qi] ?? null, isCorrect: ok }
    })
    setScore(sc)
    setMaxScore(mx)
    setCorrectCount(cor)
    setSubmitted(true)
    resultApi.submit(lessonId || '', {
      card_id: card.id,
      lesson_slug: lessonSlug,
      score: sc,
      max_score: mx,
      total: questions.length,
      correct: cor,
      detail,
    })
  }

  if (questions.length === 0) {
    return <div className="p-4 rounded-lg bg-gray-50 border border-dashed border-gray-200 text-gray-400 text-sm text-center">Chưa có câu hỏi.</div>
  }

  return (
    <div className="rounded-xl bg-blue-50/40 border border-blue-100 p-4">
      <h4 className="text-base font-bold text-blue-900 mb-4 flex items-center gap-2">🎯 {card.title || 'Câu hỏi kiểm tra'}</h4>

      <div className="space-y-4">
        {questions.map((q: any, qi: number) => {
          const given = answers[qi]
          const ok = submitted && isCorrect(q, given)
          const wrong = submitted && !isCorrect(q, given)
          return (
            <div
              key={qi}
              className={`bg-white rounded-lg border p-4 ${submitted ? (ok ? 'border-green-300' : 'border-red-200') : 'border-blue-100'}`}
            >
              <div className="flex items-start gap-2 mb-2">
                <span className="text-sm font-bold text-blue-700 shrink-0">Câu {qi + 1}.</span>
                <div className="flex-1 text-gray-800 text-sm">
                  <KaTeXRenderer content={q.question || ''} />
                </div>
                {submitted && (ok ? <CheckCircle2 size={18} className="text-green-600 shrink-0" /> : <XCircle size={18} className="text-red-500 shrink-0" />)}
              </div>

              {/* Trắc nghiệm */}
              {q.type === 'multiple_choice' && (
                <div className="space-y-1.5">
                  {(q.options || []).map((opt: string, oi: number) => {
                    const sel = Number(given) === oi
                    const isAns = submitted && Number(q.correct) === oi
                    return (
                      <label
                        key={oi}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer text-sm ${
                          isAns ? 'bg-green-50' : sel ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input type="radio" name={`q-${card.id}-${qi}`} disabled={submitted} checked={sel} onChange={() => setAns(qi, oi)} />
                        <span className="text-gray-700"><KaTeXRenderer content={opt} /></span>
                        {isAns && <span className="text-green-600 text-xs font-bold">✓ đáp án</span>}
                      </label>
                    )
                  })}
                </div>
              )}

              {/* Đúng/Sai */}
              {q.type === 'true_false' && (
                <div className="flex gap-3">
                  {[true, false].map((val) => (
                    <label key={String(val)} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer text-sm ${given === val ? 'bg-blue-50' : 'hover:bg-gray-50'} ${submitted && q.correct === val ? 'bg-green-50' : ''}`}>
                      <input type="radio" name={`q-${card.id}-${qi}`} disabled={submitted} checked={given === val} onChange={() => setAns(qi, val)} />
                      {val ? 'Đúng' : 'Sai'}
                    </label>
                  ))}
                </div>
              )}

              {/* Trả lời ngắn */}
              {q.type === 'fill_blank' && (
                <input
                  type="text"
                  disabled={submitted}
                  value={given || ''}
                  onChange={(e) => setAns(qi, e.target.value)}
                  placeholder="Nhập câu trả lời..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              )}

              {/* Giải thích sau khi nộp */}
              {submitted && (q.explanation || wrong) && (
                <div className="mt-2 text-xs bg-cyan-50 border border-cyan-100 rounded-lg p-2 text-cyan-800">
                  {q.type === 'fill_blank' && q.answer && <p>Đáp án: <strong>{q.answer}</strong></p>}
                  {q.explanation && <span><KaTeXRenderer content={'💡 ' + q.explanation} /></span>}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {!submitted ? (
        <button onClick={handleSubmit} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700">
          <Send size={16} /> Nộp bài
        </button>
      ) : (
        <div className="mt-4 p-3 rounded-xl bg-white border border-blue-200 text-center">
          <p className="text-lg font-black text-blue-900">
            Điểm: {score.toFixed(2)} / {maxScore.toFixed(2)}
          </p>
          <p className="text-sm text-gray-500">Đúng {correctCount}/{questions.length} câu</p>
        </div>
      )}
    </div>
  )
}

export default QuizPlayer
